// src/components/user/Masivo/DateSelector.jsx
import React, { useState, useEffect } from 'react';
import { Calendar, Clock, AlertCircle, ChevronLeft, ChevronRight } from 'lucide-react';

function DateSelector({ selectedDate, onDateChange }) {
  const [minDate, setMinDate] = useState('');
  const [maxDate, setMaxDate] = useState('');
  const [currentHour, setCurrentHour] = useState(null);
  const [canSelectToday, setCanSelectToday] = useState(true);
  const [showCalendar, setShowCalendar] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth());
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());

  const monthNames = [
    'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
  ];

  const monthNamesShort = [
    'Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun',
    'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'
  ];

  const dayNames = ['D', 'L', 'M', 'M', 'J', 'V', 'S'];

  useEffect(() => {
    // Get current Peru time from backend or use browser time
    const now = new Date();
    const hour = now.getHours();
    
    setCurrentHour(hour);
    setCanSelectToday(hour < 15); // Before 3pm
    
    // Calculate min date
    const min = new Date(now);
    if (hour >= 15) {
      // After 3pm, minimum is tomorrow
      min.setDate(min.getDate() + 1);
    }
    min.setHours(0, 0, 0, 0);
    
    // Calculate max date (30 days ahead)
    const max = new Date(now);
    max.setDate(max.getDate() + 30);
    max.setHours(23, 59, 59, 999);
    
    setMinDate(formatDateForInput(min));
    setMaxDate(formatDateForInput(max));
  }, []);

  function formatDateForInput(date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  function formatDateForDisplay(dateString) {
    if (!dateString) return '';
    const date = new Date(dateString + 'T00:00:00');
    const options = { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    };
    return date.toLocaleDateString('es-PE', options);
  }

  function formatDateDDMMYYYY(isoDate) {
    if (!isoDate) return '';
    const [year, month, day] = isoDate.split('-');
    return `${day}/${month}/${year}`;
  }

  function getDaysInMonth(month, year) {
    return new Date(year, month + 1, 0).getDate();
  }

  function getFirstDayOfMonth(month, year) {
    return new Date(year, month, 1).getDay();
  }

  function generateCalendarDays() {
    const daysInMonth = getDaysInMonth(currentMonth, currentYear);
    const firstDay = getFirstDayOfMonth(currentMonth, currentYear);
    const days = [];

    for (let i = 0; i < firstDay; i++) {
      days.push(null);
    }

    for (let day = 1; day <= daysInMonth; day++) {
      days.push(day);
    }

    return days;
  }

  function handleDateSelect(day) {
    if (!day) return;

    const year = currentYear;
    const month = String(currentMonth + 1).padStart(2, '0');
    const dayPadded = String(day).padStart(2, '0');
    const dateString = `${year}-${month}-${dayPadded}`;
    
    // Check if date is within valid range
    if (dateString >= minDate && dateString <= maxDate) {
      onDateChange(dateString);
      setShowCalendar(false);
    }
  }

  function isSelectedDate(day) {
    if (!day || !selectedDate) return false;
    const [year, month, dayOfMonth] = selectedDate.split('-').map(Number);
    return (
      dayOfMonth === day &&
      (month - 1) === currentMonth &&
      year === currentYear
    );
  }

  function isDateDisabled(day) {
    if (!day) return true;
    
    const year = currentYear;
    const month = String(currentMonth + 1).padStart(2, '0');
    const dayPadded = String(day).padStart(2, '0');
    const dateString = `${year}-${month}-${dayPadded}`;
    
    return dateString < minDate || dateString > maxDate;
  }

  function goToPreviousMonth() {
    if (currentMonth === 0) {
      setCurrentMonth(11);
      setCurrentYear(currentYear - 1);
    } else {
      setCurrentMonth(currentMonth - 1);
    }
  }

  function goToNextMonth() {
    if (currentMonth === 11) {
      setCurrentMonth(0);
      setCurrentYear(currentYear + 1);
    } else {
      setCurrentMonth(currentMonth + 1);
    }
  }

  const calendarDays = generateCalendarDays();

  return (
    <div className="bg-white rounded-2xl border-2 border-slate-200 p-6 sm:p-8">
      <div className="flex items-start gap-4 mb-6">
        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-brand-primary to-[#E6B43C] flex items-center justify-center flex-shrink-0">
          <Calendar className="w-6 h-6 text-white" />
        </div>
        <div className="flex-1">
          <h2 className="text-xl sm:text-2xl font-extrabold text-brand-primary font-montserrat mb-2">
            Paso 1: Fecha de Entrega
          </h2>
          <p className="text-sm sm:text-base text-slate-600 font-montserrat">
            Selecciona la fecha para todas las entregas de este lote
          </p>
        </div>
      </div>

      {/* Time-based alert */}
      {currentHour !== null && (
        <div className={`rounded-xl p-4 mb-6 border-l-4 ${
          canSelectToday 
            ? 'bg-brand-green/10 border-brand-green' 
            : 'bg-[#E6B43C]/10 border-[#E6B43C]'
        }`}>
          <div className="flex items-start gap-3">
            <Clock className={`w-5 h-5 flex-shrink-0 mt-0.5 ${
              canSelectToday ? 'text-brand-green' : 'text-[#E6B43C]'
            }`} />
            <div>
              <p className="text-sm font-semibold text-slate-900 font-montserrat">
                {canSelectToday 
                  ? `✅ Entregas para HOY disponibles hasta las 1:00 PM (son las ${currentHour}:00)`
                  : `⏰ Ya pasaron las 1:00 PM - Entregas desde mañana`
                }
              </p>
              {canSelectToday && (
                <p className="text-xs text-slate-600 font-montserrat mt-1">
                  Tienes {13 - currentHour} hora(s) para agendar entregas del día de hoy
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Custom calendar picker */}
      <div className="space-y-3">
        <label className="block text-sm font-bold text-slate-700 font-montserrat">
          Selecciona la fecha
        </label>
        
        <div className="relative">
          <button
            type="button"
            onClick={() => setShowCalendar(!showCalendar)}
            className="w-full rounded-xl border-2 border-slate-300 bg-white px-4 py-3 text-left focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-transparent transition-all flex items-center justify-between font-montserrat hover:border-brand-primary"
          >
            <span className="text-slate-800 font-medium">
              {selectedDate ? formatDateDDMMYYYY(selectedDate) : 'Selecciona una fecha'}
            </span>
            <Calendar className="w-5 h-5 text-slate-400" />
          </button>

          {showCalendar && (
            <div className="absolute top-full left-0 mt-2 w-full bg-white rounded-xl shadow-2xl border-2 border-slate-200 p-4 z-50">
              {/* Month/Year Header */}
              <div className="flex items-center justify-between mb-4">
                <button
                  type="button"
                  onClick={goToPreviousMonth}
                  className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                >
                  <ChevronLeft className="w-5 h-5 text-slate-600" />
                </button>
                
                <div className="text-center">
                  <p className="text-sm font-bold text-slate-800 font-montserrat">
                    {monthNames[currentMonth]} {currentYear}
                  </p>
                </div>
                
                <button
                  type="button"
                  onClick={goToNextMonth}
                  className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                >
                  <ChevronRight className="w-5 h-5 text-slate-600" />
                </button>
              </div>

              {/* Day Names */}
              <div className="grid grid-cols-7 gap-1 mb-2">
                {dayNames.map((day) => (
                  <div key={day} className="text-center text-xs font-semibold text-slate-500 py-1 font-montserrat">
                    {day}
                  </div>
                ))}
              </div>

              {/* Calendar Days */}
              <div className="grid grid-cols-7 gap-1">
                {calendarDays.map((day, index) => {
                  const isSelected = isSelectedDate(day);
                  const isDisabled = isDateDisabled(day);
                  
                  return (
                    <button
                      key={index}
                      type="button"
                      onClick={() => handleDateSelect(day)}
                      disabled={isDisabled || !day}
                      className={`
                        aspect-square rounded-lg text-sm transition-all font-montserrat
                        ${!day ? 'invisible' : ''}
                        ${isDisabled ? 'text-slate-300 cursor-not-allowed' : ''}
                        ${isSelected ? 'bg-brand-primary text-white font-bold shadow-md' : ''}
                        ${!isSelected && !isDisabled && day ? 'text-slate-700 hover:bg-brand-primary/10 hover:text-brand-primary' : ''}
                      `}
                    >
                      {day}
                    </button>
                  );
                })}
              </div>

              {/* Footer */}
              <div className="mt-4 pt-4 border-t border-slate-200">
                <button
                  type="button"
                  onClick={() => setShowCalendar(false)}
                  className="w-full py-2 text-sm text-slate-600 hover:text-slate-800 transition-colors font-montserrat font-semibold"
                >
                  Cerrar
                </button>
              </div>
            </div>
          )}
        </div>
        
        {selectedDate && (
          <div className="bg-gradient-to-r from-brand-primary/10 to-brand-green/10 rounded-xl p-4 border border-brand-primary/20">
            <p className="text-sm font-semibold text-slate-700 font-montserrat mb-1">
              Fecha seleccionada:
            </p>
            <p className="text-lg font-extrabold text-brand-primary font-montserrat capitalize">
              {formatDateForDisplay(selectedDate)}
            </p>
          </div>
        )}
      </div>

      {/* Info panel */}
      <div className="mt-6 bg-slate-50 rounded-xl p-4 border border-slate-200">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-slate-500 flex-shrink-0 mt-0.5" />
          <div className="text-xs text-slate-600 font-montserrat space-y-1">
            <p>• Puedes agendar entregas hasta <strong>30 días adelante</strong></p>
            <p>• Todas las entregas del archivo usarán la misma fecha</p>
            <p>• Puedes cambiar la fecha en cualquier momento</p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default DateSelector;